import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Link, TextField, Typography } from '@mui/material';
import SixDigitOtpInput from '../../components/common/SixDigitOtpInput';
import { toast } from 'react-toastify';
import FooterTrustChat from '../../components/common/FooterTrustChat';
import ButtonMain from '../../components/common/ButtonMain';
import {
  clearTwoFactorLogin,
  verifyTwoFactorLoginAPI,
} from '../../store/features/loginUser';
import './style.css';

function getErrorMessage(payload) {
  if (!payload) return 'Invalid verification code. Please try again.';
  if (typeof payload === 'string') return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    'Invalid verification code. Please try again.'
  );
}

function LoginTwoFactor({ rememberMe, onVerified }) {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [codeError, setCodeError] = useState('');

  const currentLanguage = useSelector((state) => state.language.currentLanguage);
  const t = useSelector((state) => state.language.translations[currentLanguage]);
  const { twoFactorToken, verifyTwoFactorLoading } = useSelector(
    (state) => state.loginToken,
  );

  const handleOtpChange = (value) => {
    setOtp(value);
    if (codeError) setCodeError('');
  };

  const handleBack = () => {
    dispatch(clearTwoFactorLogin());
    setOtp('');
    setBackupCode('');
    setUseBackupCode(false);
    setCodeError('');
  };

  const handleVerify = async () => {
    const code = useBackupCode ? backupCode.trim() : otp.trim();

    if (!twoFactorToken) {
      toast.error('Your verification session expired. Please sign in again.');
      handleBack();
      return;
    }

    if (!code) {
      setCodeError(
        useBackupCode
          ? 'Enter your backup code'
          : 'Enter the 6-digit code from your authenticator app',
      );
      return;
    }

    if (!useBackupCode && code.length !== 6) {
      setCodeError('Enter a 6-digit code');
      return;
    }

    setCodeError('');

    const action = await dispatch(
      verifyTwoFactorLoginAPI({
        twoFactorToken,
        code,
        rememberMe,
      }),
    );

    if (verifyTwoFactorLoginAPI.fulfilled.match(action)) {
      const payload = action.payload;
      if (payload?.success && payload?.user) {
        onVerified?.(payload);
        return;
      }
      const message = getErrorMessage(payload);
      setCodeError(message);
      toast.error(message);
      return;
    }

    const message = getErrorMessage(action.payload);
    setCodeError(message);
    toast.error(message);
  };

  const canSubmit = useBackupCode
    ? Boolean(backupCode.trim()) && !verifyTwoFactorLoading
    : otp.trim().length === 6 && !verifyTwoFactorLoading;

  return (
    <>
      <div className="adminPanelMainDiv__login">
        <div className="inputDivStyle-login">
          <div className="textDiv__login">
            <p className="text__create">Two-factor authentication</p>
            <p className="text__start">
              {useBackupCode
                ? 'Enter one of your backup codes. Each code can only be used once.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
          </div>

          <Box sx={{ mb: 3 }}>
            {!useBackupCode ? (
              <SixDigitOtpInput
                value={otp}
                onChange={handleOtpChange}
                length={6}
                autoFocus
                disabled={verifyTwoFactorLoading}
              />
            ) : (
              <TextField
                fullWidth
                value={backupCode}
                onChange={(e) => {
                  setBackupCode(e.target.value);
                  if (codeError) setCodeError('');
                }}
                placeholder="8F3K-9J2M-4L6P"
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontFamily: 'Inter, sans-serif',
                  },
                }}
              />
            )}

            {codeError ? (
              <Typography
                sx={{
                  mt: 1.5,
                  color: '#DC2626',
                  fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {codeError}
              </Typography>
            ) : null}
          </Box>

          <Typography
            sx={{
              fontSize: 13,
              color: '#405261',
              fontFamily: 'Inter, sans-serif',
              mb: 2,
            }}
          >
            {useBackupCode ? (
              <>
                Prefer your authenticator app?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    setUseBackupCode(false);
                    setBackupCode('');
                    setCodeError('');
                  }}
                  sx={{ fontWeight: 500, textDecoration: 'none' }}
                >
                  Use 6-digit code
                </Link>
              </>
            ) : (
              <>
                Lost your device?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    setUseBackupCode(true);
                    setOtp('');
                    setCodeError('');
                  }}
                  sx={{ fontWeight: 500, textDecoration: 'none' }}
                >
                  Use a backup code
                </Link>
              </>
            )}
          </Typography>

          <div className="btn-class">
            <ButtonMain
              title={verifyTwoFactorLoading ? t.BTN_LOADING : 'Verify'}
              onClick={handleVerify}
              className={canSubmit ? 'buttonStyleEnable' : 'buttonStyleDisabled'}
              disabled={!canSubmit}
            />
            <p className="a__singIn" style={{ marginTop: 16 }}>
              <Link
                component="button"
                type="button"
                onClick={handleBack}
                sx={{
                  textDecoration: 'none',
                  color: '#148dfd',
                  fontWeight: 500,
                  fontSize: 14,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  p: 0,
                }}
              >
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
      <FooterTrustChat />
    </>
  );
}

LoginTwoFactor.propTypes = {
  rememberMe: PropTypes.bool,
  onVerified: PropTypes.func,
};

LoginTwoFactor.defaultProps = {
  rememberMe: false,
  onVerified: undefined,
};

export default LoginTwoFactor;
